import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Copy, Check } from 'lucide-react';

/**
 * CodeBlock — dark usage snippet with a copy button.
 *
 * Replaces fourteen inline <pre> blocks written in two competing styles.
 * `navigator.clipboard` is unavailable on insecure origins, so the copy
 * falls back to a hidden textarea + execCommand.
 */

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to the legacy path */
    }
  }

  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
};

const CodeBlock = ({ code, title }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = async () => {
    const ok = await copyText(code);
    if (!ok) return;
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      {title && (
        <h3
          className="typo-h5 ds-page__card-title"
          style={{ paddingBottom: 'var(--space-ui-small-l)' }}
        >
          {title}
        </h3>
      )}
      <div className="ds-page__code">
        <button
          type="button"
          onClick={handleCopy}
          className={`typo-body-sm ds-page__code-copy${copied ? ' ds-page__code-copy--copied' : ''}`}
          aria-label={copied ? 'Copied' : 'Copy code'}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        <pre className="typo-body ds-page__code-pre">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  );
};

CodeBlock.propTypes = {
  /** Snippet body — rendered verbatim, no syntax highlighting */
  code: PropTypes.string.isRequired,
  /** Optional heading above the block */
  title: PropTypes.string,
};

export default CodeBlock;
